import React, { useState, useEffect } from 'react';
import { Layout, Card, List, Badge, Typography, Space, Input, Button, Modal, Tabs, Tag, Table, message } from 'antd';
import { socketService } from '../services/socketService';
import { useChat } from '../context/ChatContext';
import { 
    MonitorOutlined, 
    HistoryOutlined, 
    LockOutlined,
    UserOutlined,
    MessageOutlined,
    ClockCircleOutlined,
    DeleteOutlined,
    SendOutlined
} from '@ant-design/icons';

const { Header, Content } = Layout;
const { Title, Text } = Typography;

const Admin = () => {
    const { darkMode } = useChat();
    const [isAuthenticated, setIsAuthenticated] = useState(false);
    const [password, setPassword] = useState('');
    const [activeMatches, setActiveMatches] = useState([]);
    const [archive, setArchive] = useState([]);
    const [selectedChat, setSelectedChat] = useState(null);
    const [isArchiveModalVisible, setIsArchiveModalVisible] = useState(false);
    const [adminMsg, setAdminMsg] = useState('');

    useEffect(() => {
        socketService.onAdminMatches((matches) => {
            setActiveMatches(matches);
            // Update selected chat if it's active to show new messages
            if (selectedChat) {
                const updatedMatch = matches.find(m => m.matchId === selectedChat.matchId);
                if (updatedMatch) setSelectedChat(updatedMatch);
            }
        });

        socketService.onArchiveData((data) => {
            setArchive(data);
        });

        socketService.onAdminError((err) => {
            message.error(err);
        });

        socketService.onAdminSuccess((msg) => {
            message.success(msg);
        });

        return () => {};
    }, [selectedChat]);

    const handleLogin = () => {
        socketService.adminJoin(password);
        setIsAuthenticated(true);
    };

    const fetchArchive = () => {
        socketService.getArchive();
    };

    const handleClearArchive = () => {
        Modal.confirm({
            title: 'Are you sure you want to clear the entire archive?',
            icon: <DeleteOutlined style={{ color: 'red' }} />,
            okText: 'Yes, Clear All',
            okType: 'danger',
            cancelText: 'No',
            onOk() {
                socketService.clearArchive();
            },
        });
    };

    const showChatDetails = (chat) => {
        setSelectedChat(chat);
        setIsArchiveModalVisible(true);
    };

    const sendAdminMessage = () => {
        if (!adminMsg.trim() || !selectedChat?.matchId) return;
        socketService.adminSendMessage(selectedChat.matchId, adminMsg);
        setAdminMsg('');
    };

    if (!isAuthenticated) {
        return (
            <div style={{ display: 'flex', justifyContent: 'center', alignItems: 'center', height: '100vh' }}>
                <Card title="Admin Login" style={{ width: 300 }}>
                    <Space direction="vertical" style={{ width: '100%' }}>
                        <Input.Password 
                            prefix={<LockOutlined />} 
                            placeholder="Password" 
                            value={password}
                            onChange={(e) => setPassword(e.target.value)}
                            onPressEnter={handleLogin}
                        />
                        <Button type="primary" block onClick={handleLogin}>Login</Button>
                    </Space>
                </Card>
            </div>
        );
    }

    const archiveColumns = [
        {
            title: 'Started At',
            dataIndex: 'started_at',
            key: 'started_at',
            render: (date) => new Date(date).toLocaleString(),
            sorter: (a, b) => new Date(a.started_at) - new Date(b.started_at)
        },
        {
            title: 'Users',
            key: 'users',
            render: (_, record) => (
                <Space>
                    <Tag color="blue">{record.user1_info?.gender || 'N/A'}</Tag>
                    <Text>vs</Text>
                    <Tag color="pink">{record.user2_info?.gender || 'N/A'}</Tag>
                </Space>
            )
        },
        {
            title: 'Messages',
            key: 'msg_count',
            render: (_, record) => record.conversation?.length || 0
        },
        {
            title: 'Action',
            key: 'action',
            render: (_, record) => (
                <Button size="small" onClick={() => showChatDetails(record)}>View</Button>
            )
        }
    ];

    return (
        <Layout style={{ minHeight: '100vh', background: 'transparent' }}>
            <Header style={{ background: darkMode ? '#141414' : '#fff', padding: '0 20px', borderBottom: '1px solid #f0f0f0' }}>
                <Title level={4} style={{ margin: '15px 0' }}>Admin Dashboard</Title>
            </Header>
            <Content style={{ padding: '20px' }}>
                <Tabs defaultActiveKey="realtime" onChange={(key) => key === 'archive' && fetchArchive()}>
                    <Tabs.TabPane tab={<span><MonitorOutlined />Realtime</span>} key="realtime">
                        <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fill, minmax(300px, 1fr))', gap: '20px' }}>
                            {activeMatches.length === 0 && <Text type="secondary">No active matches found.</Text>}
                            {activeMatches.map(match => (
                                <Card 
                                    key={match.matchId}
                                    title={
                                        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                                            <Badge status="processing" text="Active" />
                                            <Text type="secondary" style={{ fontSize: '10px' }}>{match.matchId.substr(0,8)}</Text>
                                        </div>
                                    }
                                    size="small"
                                    hoverable
                                    onClick={() => showChatDetails(match)}
                                >
                                    <div style={{ marginBottom: 10 }}>
                                        <Space split={<Divider type="vertical" />}>
                                            <Tag color="blue">{match.u1.info.gender} ({match.u1.info.age})</Tag>
                                            <Tag color="pink">{match.u2.info.gender} ({match.u2.info.age})</Tag>
                                        </Space>
                                    </div>
                                    <List
                                        size="small"
                                        dataSource={match.messages.slice(-3)}
                                        renderItem={msg => (
                                            <List.Item style={{ padding: '4px 0', border: 'none' }}>
                                                <Text type="secondary" style={{ fontSize: '12px' }} ellipsis>
                                                    <Text strong style={{ fontSize: '10px' }}>{msg.from === 'admin' ? 'ADMIN' : msg.from.substr(0,4)}:</Text> {msg.text}
                                                </Text>
                                            </List.Item>
                                        )}
                                    />
                                </Card>
                            ))}
                        </div>
                    </Tabs.TabPane>
                    <Tabs.TabPane tab={<span><HistoryOutlined />Archive</span>} key="archive">
                        <div style={{ marginBottom: 15, textAlign: 'right' }}>
                            <Button danger icon={<DeleteOutlined />} onClick={handleClearArchive}>Clear All Archive</Button>
                        </div>
                        <Table 
                            dataSource={archive} 
                            columns={archiveColumns} 
                            rowKey="id" 
                            size="small"
                            pagination={{ pageSize: 15 }}
                        />
                    </Tabs.TabPane>
                </Tabs>
            </Content>

            <Modal
                title={`Chat Details ${selectedChat?.matchId ? `(${selectedChat.matchId.substr(0,8)})` : ''}`}
                open={isArchiveModalVisible}
                onCancel={() => setIsArchiveModalVisible(false)}
                footer={null}
                width={600}
            >
                {selectedChat && (
                    <div style={{ display: 'flex', flexDirection: 'column', height: '70vh' }}>
                        <div style={{ marginBottom: 10, textAlign: 'center', borderBottom: '1px solid #f0f0f0', paddingBottom: 10 }}>
                            <Text type="secondary">Started: {new Date(selectedChat.started_at).toLocaleString()}</Text>
                        </div>
                        
                        <div style={{ flex: 1, overflowY: 'auto', padding: '10px' }}>
                            <List
                                dataSource={selectedChat.conversation || selectedChat.messages || []}
                                renderItem={msg => {
                                    const isU1 = msg.from === (selectedChat.user1_session_id || selectedChat.u1?.id);
                                    const isAdmin = msg.from === 'admin';
                                    
                                    return (
                                        <div style={{ 
                                            display: 'flex', 
                                            justifyContent: isAdmin ? 'center' : (isU1 ? 'flex-start' : 'flex-end'),
                                            marginBottom: 10
                                        }}>
                                            <div style={{ 
                                                maxWidth: '80%', 
                                                padding: '8px 12px', 
                                                borderRadius: 12,
                                                background: isAdmin ? '#fffbe6' : (isU1 ? '#f0f0f0' : '#1677ff'),
                                                color: isAdmin ? '#856404' : (isU1 ? '#000' : '#fff'),
                                                border: isAdmin ? '1px solid #ffe58f' : 'none'
                                            }}>
                                                <div style={{ fontSize: '10px', opacity: 0.7, marginBottom: 4 }}>
                                                    {isAdmin ? 'ADMIN' : (isU1 ? 'User 1' : 'User 2')} • {msg.timestamp}
                                                </div>
                                                {msg.text}
                                            </div>
                                        </div>
                                    );
                                }}
                            />
                        </div>

                        {/* Only show input for active matches, not archive */}
                        {selectedChat.matchId && !selectedChat.id && (
                            <div style={{ padding: '15px 0 0 0', borderTop: '1px solid #f0f0f0' }}>
                                <Space.Compact style={{ width: '100%' }}>
                                    <Input 
                                        placeholder="Write as Admin..." 
                                        value={adminMsg}
                                        onChange={(e) => setAdminMsg(e.target.value)}
                                        onPressEnter={sendAdminMessage}
                                    />
                                    <Button type="primary" icon={<SendOutlined />} onClick={sendAdminMessage}>Send</Button>
                                </Space.Compact>
                            </div>
                        )}
                    </div>
                )}
            </Modal>
        </Layout>
    );
};

const Divider = ({ type }) => (
    <span style={{ margin: '0 8px', borderLeft: '1px solid #ddd', height: '14px' }} />
);

export default Admin;